import { makeObservable, observable, action } from "mobx";
import { BaseStore } from "./baseStore";
import { Store } from "./store";
import {
    KeyHelper,
    SessionBuilder,
    SessionCipher,
    SignalProtocolAddress
} from "@privacyresearch/libsignal-protocol-typescript";
import SignalProtocolStore from "../utils/signalprotocol";
import {
    arrayBufferToBase64,
    arrayBufferToString,
    base64ToArrayBuffer,
    stringToArrayBuffer
} from "../utils/global";

export enum E2EEStoreState {
    NOT_INITIALIZED = 1 << 0,
    FULLY_INITIALIZED = 1 << 1,
}

export class E2EEStore extends BaseStore<E2EEStoreState> {
    protocolStore: SignalProtocolStore;
    identityKeyPair: any;
    registrationId: number;

    constructor(store: Store) {
        super(E2EEStoreState.NOT_INITIALIZED, store, "red");
        makeObservable(this, {
            protocolStore: observable,
            identityKeyPair: observable,
            registrationId: observable,
            init: action,
        });
    }

    /** 🚀 Inicjalizacja po starcie aplikacji */
    async init() {
        this.protocolStore = new SignalProtocolStore();

        // 1. Identity key pair
        this.identityKeyPair = await KeyHelper.generateIdentityKeyPair();
        this.registrationId = await KeyHelper.generateRegistrationId();

        await this.protocolStore.store.set("identityKey", this.identityKeyPair);
        await this.protocolStore.store.set("registrationId", this.registrationId);

        // 2. PreKey + SignedPreKey
        const preKeyId = 1;
        const preKey = await KeyHelper.generatePreKey(preKeyId);
        await this.protocolStore.storePreKey(preKeyId, preKey);

        const signedPreKeyId = 1;
        const signedPreKey = await KeyHelper.generateSignedPreKey(this.identityKeyPair, signedPreKeyId);
        await this.protocolStore.storeSignedPreKey(signedPreKeyId, signedPreKey);

        // 3. Bundle do backendu (tylko publiczne!)
        const bundle = {
            registrationId: this.registrationId,
            identityKey: arrayBufferToBase64(this.identityKeyPair.pubKey),
            preKey: arrayBufferToBase64(preKey.keyPair.pubKey),
            preKeyId,
            signedPreKey: arrayBufferToBase64(signedPreKey.keyPair.pubKey),
            signature: arrayBufferToBase64(signedPreKey.signature),
            signedPreKeyId,
        };

        console.log("📦 local bundle", bundle);

        await fetch(`${this.store.apiURL}/auth/e2ee/${this.store.user.user.id}`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${this.store.token}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(bundle),
        });

        this.addState(E2EEStoreState.FULLY_INITIALIZED);
        this.log("🔑 E2EE initialized with fresh keys");
    }

    /** 📥 Pobierz bundle usera i skonwertuj do X3DH */
    async fetchUserKeys(remoteUserId: string) {
        const res = await fetch(`${this.store.apiURL}/auth/e2ee/${remoteUserId}`, {
            headers: { Authorization: `Bearer ${this.store.token}` },
        });

        if (!res.ok) throw new Error(`Brak kluczy dla usera ${remoteUserId}`);

        const resultData = await res.json();
        const data = resultData.data;

        return {
            registrationId: data.registrationId,
            identityKey: new Uint8Array(base64ToArrayBuffer(data.identityKey)),
            preKey: {
                keyId: data.preKeyId,
                publicKey: new Uint8Array(base64ToArrayBuffer(data.preKey)),
            },
            signedPreKey: {
                keyId: data.signedPreKeyId,
                publicKey: new Uint8Array(base64ToArrayBuffer(data.signedPreKey)),
                signature: new Uint8Array(base64ToArrayBuffer(data.signature)),
            },
        };
    }

    /** ✉️ Tworzenie sesji */
    async initSession(remoteUserId: string, bundle: any, deviceId: number) {
        const address = new SignalProtocolAddress(remoteUserId, deviceId);
        const builder = new SessionBuilder(this.protocolStore, address);

        await builder.processPreKey(bundle);
        this.log(`✅ Session with ${remoteUserId}.${deviceId} initialized`);
    }

    /** 🔐 Szyfrowanie */
    async encrypt(remoteUserId: string, deviceId: number, plaintext: string) {
        const address = new SignalProtocolAddress(remoteUserId, deviceId);
        const cipher = new SessionCipher(this.protocolStore, address);

        const messageBuffer = stringToArrayBuffer(plaintext);
        return cipher.encrypt(messageBuffer);
    }

    /** 🔓 Deszyfrowanie */
    async decrypt(remoteUserId: string, deviceId: number, msg: any) {
        const address = new SignalProtocolAddress(remoteUserId, deviceId);
        const cipher = new SessionCipher(this.protocolStore, address);

        let decrypted: ArrayBuffer;
        if (msg.type === 3) {
            decrypted = await cipher.decryptPreKeyWhisperMessage(msg.body, "binary");
        } else if (msg.type === 1) {
            decrypted = await cipher.decryptWhisperMessage(msg.body, "binary");
        } else {
            throw new Error("Nieznany typ wiadomości");
        }

        return arrayBufferToString(decrypted);
    }

    /** 🔓 Deszyfrowanie wiadomości przy odbiorze */
    async receiveMessage(
        senderId: string,
        deviceId: number,
        hashContent: { type: number; body: string }
    ): Promise<string> {
        if (!hashContent) throw new Error("Brak hashContent");

        const bodyBuffer = base64ToArrayBuffer(hashContent.body);

        const address = new SignalProtocolAddress(senderId, deviceId);
        const cipher = new SessionCipher(this.protocolStore, address);

        let decrypted: ArrayBuffer;
        if (hashContent.type === 3) {
            decrypted = await cipher.decryptPreKeyWhisperMessage(bodyBuffer, "binary");
        } else if (hashContent.type === 1) {
            decrypted = await cipher.decryptWhisperMessage(bodyBuffer, "binary");
        } else {
            throw new Error("Nieznany typ wiadomości: " + hashContent.type);
        }

        return arrayBufferToString(decrypted);
    }

    /** 🚪 Logout */
    logout(): void {
        this.setState(E2EEStoreState.NOT_INITIALIZED);
        this.protocolStore = new SignalProtocolStore();
    }

    /** ♻️ Reload */
    async reload() {
        await this.init();
    }

    isReady(): boolean {
        return this.hasState(E2EEStoreState.FULLY_INITIALIZED);
    }
}
